import * as React from 'react'
import moment from 'moment'
import Helmet from 'react-helmet'
import AppHeader from '../../../components/AppView/Header'
import { AppMainView, AppView } from '../../../components/AppView'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { inject } from 'mobx-react'
import { Button, Popover, Icon, Alert, message, Skeleton, Row, Select, Drawer, List } from 'antd'
import { NoPermissionsResult } from '../../../components/NoPermissionsResult'
import { ExternalSourceCategoryNames, PermissionNames } from '@getgreenline/homi-shared'
import { parseErrorMsg, filterOption } from '../../../utilities/helpers'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import { IntegrationCard } from './IntegrationCard'
import { ExternalSourceAPI, ExternalSourceModels } from '@getgreenline/external-sources'
import { BLAZE_ECOMMERCE, BLAZE_COMPANY } from '../../../constants/general'
import { HelpLinks } from '../../../constants/ExternalLinks'
interface Props {
  currentUserStore: CurrentUserStore
  currentCompanyStore: CurrentCompanyStore
}

export type IAllAccessibleIntegration = Omit<
  ExternalSourceModels.IExternalSourceContract,
  'id' | 'hexColor' | 'isEnabled'
> & {
  id: null
  isEnabled: null
}

interface State {
  isLoading: boolean
  selectedExternalSource?:
    | ExternalSourceModels.ICompanyExternalSourceContract
    | IAllAccessibleIntegration
  selectedCategoryName: ExternalSourceCategoryNames | 'ALL'
  selectedExternalSourceName?: string
  mappedCompanyExternalSources: Map<
    string,
    ExternalSourceModels.ICompanyExternalSourceContract | IAllAccessibleIntegration
  >
}

@inject('currentCompanyStore', 'currentUserStore')
export class Integrations extends React.Component<Props, State> {
  company = this.props.currentCompanyStore!.company!
  user = this.props.currentUserStore!.currentUser!
  locations = this.props.currentCompanyStore!.locations || []

  allAccessibleIntegrations: IAllAccessibleIntegration[] = [
    {
      id: null,
      name: 'Merrco',
      logoImageUrl:
        'https://res.cloudinary.com/get-greenline/image/upload/v1594670857/vot31i18mfqyvmoz3ous.png',
      description: `Accept credit and debit card payments with Canada's first payment processor for the cannabis industry.`,
      categoryName: ExternalSourceCategoryNames.PAYMENT_PROCESSING,
      setupUrl: null,
      email: 'info@merrco.com',
      isEnabled: null,
      homepage: 'https://www.merrco.com/',
      permissions: [],
    },
    {
      id: null,
      name: 'Moneris',
      logoImageUrl:
        'https://res.cloudinary.com/get-greenline/image/upload/v1594670883/bklylffvdfz1ebsqqdez.jpg',
      description: `Accept credit and debit card payments with one of Canada's larges payment processors.`,
      categoryName: ExternalSourceCategoryNames.PAYMENT_PROCESSING,
      setupUrl: null,
      email: null,
      isEnabled: null,
      homepage: 'https://www.moneris.com/',
      permissions: [],
    },
    {
      id: null,
      name: 'Leafly',
      logoImageUrl:
        'https://res.cloudinary.com/get-greenline/image/upload/v1594668987/rfexldveqfv2uyakaoti.png',
      description: `Learn all about cannabis and find the
      best strains and products in medical &
      recreational marijuana stores near you.`,
      categoryName: ExternalSourceCategoryNames.ONLINE_MENU,
      setupUrl: HelpLinks.LEAFLY,
      email: 'onboarding@getgreenline.co',
      isEnabled: null,
      homepage: 'https://leafly.com',
      permissions: [],
    },
    {
      id: null,
      name: 'QuickBooks',
      logoImageUrl:
        'https://res.cloudinary.com/get-greenline/image/upload/v1594669036/nt2wcaljz5mfaux9wkse.png',
      description: `Quickbooks' all in one accounting
      software is a great fit for businesses
      of all sizes. ${BLAZE_COMPANY}'s integration uses
      journals rather than individual/batched
      transactions.`,
      categoryName: ExternalSourceCategoryNames.PAYMENT_PROCESSING,
      setupUrl: HelpLinks.QUICKBOOKS,
      email: 'onboarding@getgreenline.co',
      isEnabled: null,
      homepage: 'https://quickbooks.intuit.com/ca/',
      permissions: [],
    },
  ]

  constructor(props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      selectedExternalSourceName: undefined,
      selectedCategoryName: 'ALL',
      mappedCompanyExternalSources: new Map(),
    }
  }

  componentDidMount = async () => {
    this.setState({ isLoading: true })

    try {
      const { mappedCompanyExternalSources } = this.state
      const companyExternalSources = await ExternalSourceAPI.getCompanyExternalSources(
        this.company.id,
      )
      this.allAccessibleIntegrations.forEach((data) => {
        mappedCompanyExternalSources.set(data.name, data)
      })
      companyExternalSources.forEach((data) => {
        mappedCompanyExternalSources.set(data.name, data)
      })
      this.setState({
        mappedCompanyExternalSources,
      })
    } catch (error) {
      message.error(
        parseErrorMsg(error) || 'An error occurred while loading/mapping enabled external sources',
      )
    }

    this.setState({ isLoading: false })
  }

  setCompanyExternalSource = async (
    companyExternalSource: ExternalSourceModels.ICompanyExternalSourceContract,
  ) => {
    const { mappedCompanyExternalSources } = this.state
    mappedCompanyExternalSources.set(companyExternalSource.name, companyExternalSource)
    this.setState({ mappedCompanyExternalSources })
  }

  get filteredEnabledSources(): (
    | ExternalSourceModels.ICompanyExternalSourceContract
    | IAllAccessibleIntegration
  )[] {
    const { mappedCompanyExternalSources, selectedExternalSourceName, selectedCategoryName } =
      this.state

    if (mappedCompanyExternalSources === undefined) {
      return []
    }

    return [...mappedCompanyExternalSources.values()].filter((externalSource) => {
      const isIdMatching =
        selectedExternalSourceName === undefined
          ? true
          : externalSource.name === selectedExternalSourceName

      const categoryName: string = ExternalSourceCategoryNames[selectedCategoryName]

      const isCategoryMatching =
        selectedCategoryName === 'ALL' ? true : externalSource.categoryName === categoryName

      const hideExternalSource =
        externalSource.name.toLowerCase() === BLAZE_ECOMMERCE.toLowerCase() &&
        !this.props.currentCompanyStore.canViewEcommerceSettings

      return isCategoryMatching && isIdMatching && !hideExternalSource
    })
  }

  get canManageIntegrations(): boolean {
    const canManageIntegrations = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_MANAGE_INTEGRATIONS,
      this.company.id,
    )

    return canManageIntegrations
  }

  render() {
    if (!this.canManageIntegrations) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_MANAGE_INTEGRATIONS} />
    }

    const {
      isLoading,
      mappedCompanyExternalSources,
      selectedExternalSource,
      selectedExternalSourceName,
      selectedCategoryName,
    } = this.state
    return (
      <AppView column>
        <Helmet>
          <title>Integrations | Overview</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader title='Integrations'>
          <span className='spaced-button-wrapper'>
            <Button icon='question-circle' href={HelpLinks.INTEGRATIONS} target={'_blank '}>
              {'Help'}
            </Button>
            <Popover
              title='API keys'
              trigger='click'
              placement='rightBottom'
              content={
                <div>
                  <Alert
                    type='info'
                    showIcon
                    message='To grant access to your company data, copy/paste the information below and send it to your integration partner.'
                    style={{ marginBottom: 10 }}
                  />
                  <Alert
                    type='warning'
                    showIcon
                    message='Be careful with giving out your API information! It will give access sensitive product and inventory data.'
                    style={{ marginBottom: 10 }}
                  />
                  <pre style={{ wordWrap: 'break-word' }}>
                    <div>Date: {moment().format('YYYY/MM/DD hh:mma')}</div>
                    <div>Authorizing user: {this.user.email}</div>
                    <div>Company name: {this.company.name}</div>
                    <div>Company ID: {this.company.id}</div>
                    {this.locations.map((location) => (
                      <div key={location.id}>
                        {location.name}: {location.id}
                      </div>
                    ))}
                  </pre>
                </div>
              }
            >
              <Button type='primary'>
                <Icon type='api' />
                API keys
              </Button>
            </Popover>
          </span>
        </AppHeader>

        <AppMainView className='background-grey pb-4'>
          <div className='d-flex flex-wrap space-evenly white-rounded-container pb-0'>
            <Select<string | undefined>
              showSearch
              className='gr-filter-item gr-filter-gutter'
              placeholder='Name'
              allowClear
              loading={isLoading}
              dropdownMatchSelectWidth={false}
              disabled={mappedCompanyExternalSources === undefined}
              value={selectedExternalSourceName}
              onChange={(value) => this.setState({ selectedExternalSourceName: value })}
              filterOption={filterOption}
            >
              {([...mappedCompanyExternalSources.values()] || []).map(
                (enabledExternalSource, index) => (
                  <Select.Option key={index} value={enabledExternalSource.name}>
                    {enabledExternalSource.name}
                  </Select.Option>
                ),
              )}
            </Select>

            <Select<ExternalSourceCategoryNames | 'ALL'>
              showSearch
              className='gr-filter-item gr-filter-gutter'
              placeholder='Category'
              defaultValue='ALL'
              dropdownMatchSelectWidth={false}
              value={selectedCategoryName}
              onChange={(value) => this.setState({ selectedCategoryName: value })}
              filterOption={filterOption}
            >
              <Select.Option value='ALL' style={{ borderBottom: '1px solid rgba(0,0,0,.1)' }}>
                All categories
              </Select.Option>

              {Object.keys(ExternalSourceCategoryNames).map((nameKey, index) => (
                <Select.Option key={index} value={nameKey}>
                  {ExternalSourceCategoryNames[nameKey]}
                </Select.Option>
              ))}
            </Select>

            <BlazeButton
              buttonProps={{
                className: 'gr-filter-gutter text-secondary',
                onClick: () =>
                  this.setState({
                    selectedExternalSourceName: undefined,
                    selectedCategoryName: 'ALL',
                  }),
              }}
              titleProps={{ title: 'Reset' }}
            />
          </div>

          <Row type='flex' style={{ marginRight: '-1rem' }} className='intercom-pb-1'>
            {isLoading ? (
              <Skeleton active />
            ) : (
              this.filteredEnabledSources.map((enabledExternalSource) => {
                return (
                  <IntegrationCard
                    key={enabledExternalSource.id ? enabledExternalSource.id : undefined}
                    company={this.company}
                    enabledExternalSource={enabledExternalSource}
                    setSelectedExternalSource={() =>
                      this.setState({ selectedExternalSource: enabledExternalSource })
                    }
                    setCompanyExternalSource={this.setCompanyExternalSource}
                  />
                )
              })
            )}
          </Row>

          <Drawer
            visible={!!selectedExternalSource}
            title={`Permissions - ${selectedExternalSource ? selectedExternalSource.name : ''}`}
            width={350}
            onClose={() => this.setState({ selectedExternalSource: undefined })}
          >
            {selectedExternalSource && (
              <List
                bordered
                dataSource={selectedExternalSource.permissions}
                renderItem={(permission) => {
                  return <List.Item>{permission.name}</List.Item>
                }}
              />
            )}
          </Drawer>
          <div style={{ padding: '1rem' }} />
        </AppMainView>
      </AppView>
    )
  }
}
